import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Package, MapPin, X } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import Button from '../common/Button';
import axios from '../../config/axios';

const PublicShipmentTracking = () => {
  const navigate = useNavigate();
  const [shipmentId, setShipmentId] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returnId, setReturnId] = useState(null);
  const [loadingReturnId, setLoadingReturnId] = useState(false);
  const [deliveryImageModal, setDeliveryImageModal] = useState(false);
  const [selectedDeliveryImage, setSelectedDeliveryImage] = useState(null);

  // Fetch return ID when shipment status is "Returned"
  useEffect(() => {
    if (trackingData?.shipment_details?.status?.toLowerCase() === 'returned') {
      fetchReturnId(trackingData.shipment_details.shipment_id);
    }
  }, [trackingData]);

  const fetchReturnId = async (shipmentId) => {
    try {
      setLoadingReturnId(true);
      // Fetch all returns and filter by shipment_id
      const response = await axios.get('/shipments/returned', {
        params: { return_status: 'close' }
      });
      
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // Get the most recent return for this shipment
        const returns = response.data.data.filter(r => r.shipment_id === shipmentId);
        if (returns.length > 0) {
          // Sort by created_at descending and get the first one
          const sortedReturns = returns.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          );
          setReturnId(sortedReturns[0].return_id);
        }
      }
    } catch (err) {
      console.error('Error fetching return ID:', err);
      // Don't show error to user, just hide the button
      setReturnId(null);
    } finally {
      setLoadingReturnId(false);
    }
  };

  const handleViewDeliveryImage = (imageBase64) => {
    setSelectedDeliveryImage(imageBase64);
    setDeliveryImageModal(true);
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!shipmentId.trim()) {
      setError('Please enter a shipment ID');
      return;
    }

    setLoading(true);
    setError('');
    setTrackingData(null);
    setReturnId(null);

    try {
      const response = await axios.get(`/tracking/${shipmentId}`);
      if (response.data.success) {
        setTrackingData(response.data);
      } else {
        setError(response.data.error || 'Shipment not found');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tracking information');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReturn = () => {
    if (returnId) {
      navigate(`/public-view-return/${returnId}`);
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'booked': 'bg-blue-100 text-blue-800 border-blue-200',
      'dispatched': 'bg-purple-100 text-purple-800 border-purple-200',
      'received': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'delivered': 'bg-green-100 text-green-800 border-green-200',
      'returned': 'bg-red-100 text-red-800 border-red-200',
      'rejected': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPickupTypeBadgeColor = (type) => {
    const colors = {
      'pickup': 'bg-green-100 text-green-800 border-green-200',
      'parcel_shop': 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[type?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const isReturnedStatus = trackingData?.shipment_details?.status?.toLowerCase() === 'returned';

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      <section className="bg-gradient-to-r from-brand to-brand-hover text-white pt-28 pb-10 md:pt-32 md:pb-12">
        <div className="container mx-auto px-4 lg:px-6">
          <h1 className="text-2xl md:text-3xl font-semibold mb-2">Track Your Shipment</h1>
          <p className="text-blue-100 text-sm md:text-base mb-6">Enter your shipment ID for real-time tracking updates</p>
          <div className="max-w-2xl">
            <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="Enter Shipment ID (e.g., 001234)"
                className="input-clean flex-1 !bg-white"
              />
              <Button type="submit" variant="secondary" size="md" icon={Search} loading={loading} className="shrink-0">
                {loading ? 'Tracking...' : 'Track'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-6 py-8">

        {/* Error Message */}
        {error && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="bg-red-50 border border-red-200 rounded-card p-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tracking Information */}
        {trackingData && (
          <div className="space-y-4">
            {/* Shipment Details - Compact */}
            <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100 flex-wrap gap-3">
                <h2 className="text-lg font-semibold text-dark flex items-center gap-2">
                  <Package className="w-5 h-5 text-brand" />
                  Shipment Details
                </h2>
                {isReturnedStatus && returnId && (
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Eye}
                    onClick={handleViewReturn}
                    disabled={loadingReturnId}
                    loading={loadingReturnId}
                  >
                    {loadingReturnId ? 'Loading...' : 'View Return'}
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                <div className="bg-gray-50 rounded-btn p-3 border border-gray-100">
                  <label className="text-xs text-secondary font-medium block mb-1">Shipment ID</label>
                  <p className="text-sm font-semibold text-brand font-mono">
                    #{trackingData.shipment_details.shipment_id}
                  </p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <label className="text-xs text-gray-600 font-semibold block mb-1">Status</label>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${getStatusBadgeColor(trackingData.shipment_details.status)}`}>
                    {trackingData.shipment_details.status}
                  </span>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <label className="text-xs text-gray-600 font-semibold block mb-1">Articles</label>
                  <p className="text-sm font-bold text-gray-800">{trackingData.shipment_details.no_of_articles || 0}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <label className="text-xs text-gray-600 font-semibold block mb-1">Weight</label>
                  <p className="text-sm font-bold text-gray-800">{trackingData.shipment_details.weight || 'N/A'} kg</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <label className="text-xs text-gray-600 font-semibold block mb-1">Vehicle</label>
                  <p className="text-sm font-bold text-gray-800">{trackingData.shipment_details.vehicle_number || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <label className="text-xs text-gray-600 font-semibold block mb-1">Created</label>
                  <p className="text-sm font-bold text-gray-800">
                    {new Date(trackingData.shipment_details.created_at).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {/* Sender & Receiver Info - Compact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                <div className="bg-brand-light rounded p-3 border border-blue-100">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-1">
                    <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                    From
                  </h3>
                  <p className="text-xs text-gray-700 mb-1">
                    <span className="font-semibold">Name:</span> {trackingData.shipment_details.sender_name}
                  </p>
                  <p className="text-xs text-gray-700 line-clamp-2">
                    <span className="font-semibold">Address:</span> {trackingData.shipment_details.sender_address}
                  </p>
                </div>
                <div className="bg-green-50 rounded p-3 border border-green-100">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-1">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    To
                  </h3>
                  <p className="text-xs text-gray-700 mb-1">
                    <span className="font-semibold">Name:</span> {trackingData.shipment_details.receiver_name}
                  </p>
                  <p className="text-xs text-gray-700 line-clamp-2">
                    <span className="font-semibold">Address:</span> {trackingData.shipment_details.receiver_address}
                  </p>
                </div>
              </div>
            </div>

            {/* Current Location */}
            {trackingData.current_location && (
              <div className="bg-brand-light rounded-card shadow-card p-4 md:p-6 border border-blue-100">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-dark">
                  <MapPin className="w-5 h-5 text-brand" />
                  Current Location
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="bg-white rounded p-2 border border-blue-100">
                    <p className="text-xs text-gray-600 font-semibold mb-1">Location</p>
                    <p className="text-sm font-bold text-gray-800">{trackingData.current_location.location}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-blue-100">
                    <p className="text-xs text-gray-600 font-semibold mb-1">Status</p>
                    <p className="text-sm font-bold text-gray-800">{trackingData.current_location.status}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-blue-100">
                    <p className="text-xs text-gray-600 font-semibold mb-1">Updated By</p>
                    <p className="text-sm font-bold text-gray-800">{trackingData.current_location.updated_by}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-blue-100">
                    <p className="text-xs text-gray-600 font-semibold mb-1">Last Updated</p>
                    <p className="text-sm font-bold text-gray-800">
                      {new Date(trackingData.current_location.timestamp).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tracking Timeline */}
            {trackingData.tracking_timeline && trackingData.tracking_timeline.length > 0 && (
              <div className="bg-white rounded-card shadow-card border border-gray-100 p-4 md:p-6">
                <style>{`
                  @keyframes slideInDown {
                    from {
                      opacity: 0;
                      transform: translateY(-10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                  @keyframes pulse-dot {
                    0%, 100% {
                      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
                    }
                    50% {
                      box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
                    }
                  }
                  .timeline-item {
                    animation: slideInDown 0.5s ease-out forwards;
                  }
                  .timeline-item:nth-child(1) { animation-delay: 0.1s; }
                  .timeline-item:nth-child(2) { animation-delay: 0.2s; }
                  .timeline-item:nth-child(3) { animation-delay: 0.3s; }
                  .timeline-item:nth-child(4) { animation-delay: 0.4s; }
                  .timeline-item:nth-child(5) { animation-delay: 0.5s; }
                  .pulse-dot {
                    animation: pulse-dot 2s infinite;
                  }
                `}</style>
                <h2 className="text-lg font-semibold text-dark mb-4 pb-4 border-b border-gray-100">
                  Tracking Timeline
                </h2>
                <div className="space-y-3">
                  {trackingData.tracking_timeline.map((entry, index) => (
                    <div key={index} className="flex gap-3 timeline-item">
                      {/* Timeline Dot */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className={`w-8 h-8 ${index === 0 ? 'pulse-dot' : ''} bg-brand rounded-full flex items-center justify-center text-white font-bold shadow`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        {index < trackingData.tracking_timeline.length - 1 && (
                          <div className="w-0.5 flex-1 bg-brand-light mt-1 min-h-[50px]"></div>
                        )}
                      </div>

                      {/* Timeline Content */}
                      <div className="flex-1 bg-gray-50 rounded-card p-3 border border-gray-100 hover:border-brand/30 hover:shadow-card transition-all">
                        <h3 className="font-bold text-gray-900 text-sm mb-2">{entry.status}</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-gray-600 font-semibold mb-0.5">Location</p>
                            <p className="text-gray-800">{entry.location}</p>
                          </div>
                          
                          <div>
                            <p className="text-gray-600 font-semibold mb-0.5">Time</p>
                            <p className="text-gray-800">
                              {new Date(entry.timestamp).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-gray-600 font-semibold mb-0.5">Updated by</p>
                            <p className="text-gray-800">{entry.updated_by_name} ({entry.updated_by_role})</p>
                          </div>
                          
                          <div>
                            <p className="text-gray-600 font-semibold mb-0.5">Contact</p>
                            <a href={`tel:${entry.updated_by_contact}`} className="text-brand hover:text-brand-hover hover:underline">
                              {entry.updated_by_contact}
                            </a>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <p className="text-gray-600 font-semibold mb-0.5">Email</p>
                            <a href={`mailto:${entry.updated_by_email}`} className="text-brand hover:text-brand-hover hover:underline break-all">
                              {entry.updated_by_email}
                            </a>
                          </div>
                          
                          {entry.status?.toLowerCase() === 'delivered' && entry.delivery_receiver && (
                            <div>
                              <p className="text-gray-600 font-semibold mb-0.5">Delivered To</p>
                              <p className="text-gray-800">{entry.delivery_receiver}</p>
                            </div>
                          )}
                          
                          {entry.status?.toLowerCase() === 'delivered' && entry.delivery_receiver_image_base64 && (
                            <div>
                              <Button
                                variant="primary"
                                size="sm"
                                icon={Eye}
                                onClick={() => handleViewDeliveryImage(entry.delivery_receiver_image_base64)}
                              >
                                View Photo
                              </Button>
                            </div>
                          )}
                          
                          {entry.remarks && (
                            <div className="sm:col-span-2">
                              <p className="text-gray-600 font-semibold mb-0.5">Note</p>
                              <p className="text-gray-800 bg-white p-2 rounded border border-gray-300">
                                {entry.remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Shipment Selected */}
        {!trackingData && !loading && !error && (
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-card shadow-card border border-gray-100 p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-dark mb-2">Track Your Shipment</h3>
              <p className="text-secondary text-sm">
                Enter a shipment ID above to view real-time tracking information
              </p>
            </div>
          </div>
        )}
      </div>

      <Footer />
      {/* Delivery Receiver Image Modal */}
      {deliveryImageModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card shadow-strong max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-dark">Delivery Receiver Photo</h3>
                <button
                  onClick={() => setDeliveryImageModal(false)}
                  className="p-2 rounded-btn hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-secondary" />
                </button>
              </div>
              <div className="flex justify-center">
                <img
                  src={`data:image/jpeg;base64,${selectedDeliveryImage}`}
                  alt="Delivery Receiver"
                  className="max-w-full h-auto rounded-card shadow-card"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicShipmentTracking;